import { useState, useEffect, useRef } from "react";
import { Helmet } from 'react-helmet';
import AdSlot from '../../components/ads/AdSlot';
import {
  Award, Clock, CheckCircle, Users, BookOpen, TrendingUp, Star, Truck,
  GraduationCap, FileText, DollarSign, HeartHandshake, Shield,
  ChevronDown, ChevronRight, MapPin, Phone,
  ThumbsUp, ThumbsDown, Send, Loader2, Gift, ExternalLink, Building2, ArrowRight,
  Volume2, VolumeX, Volume1,
} from "lucide-react";
import { TuitionCalculator } from '../financing/FinancingPage';
import andrewPhoto    from "../../assets/peopleImage/mr_andrew_image.jpeg";
import otisPhoto      from "../../assets/peopleImage/mr_otis_imageNew.jpeg";
import hardenPhoto    from "../../assets/peopleImage/mr_harden.jpeg";
import arleanPhoto    from "../../assets/peopleImage/ms_arlene.jpeg";
import irishPhoto     from "../../assets/peopleImage/ms_irish_image.jpeg";
import armedaPhoto    from "../../assets/team/ms_armeda_pic.png";
import facilityImg2        from "../../assets/facility/Facility_Image2.jpeg";
import facilityImg4        from "../../assets/facility/Facility_Image4.jpeg";
import facilityImgStudents from "../../assets/facility/Facility_Image_Students.jpeg";
import facilityImg6   from "../../assets/facility/Facility_Image6.jpeg";
import facilityImg9   from "../../assets/facility/Facility_Image9.jpeg";
import facilityImg11  from "../../assets/facility/Facility_Image11.jpeg";
import facilityImg15  from "../../assets/facility/Facility_Image15.jpeg";
import facilityImg16  from "../../assets/facility/Facility_Image16.jpeg";
import roadReadyManualImg from "../../assets/images/road_ready_manual.jpeg";
import heroVideo      from "../../assets/videos/hero_video.mp4";
import heroTrack     from "../../assets/sounds/cst_soundTrack.mp3";

// â”€â”€ COLORS â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const C = {
  navy: "#1B2B4B", navyDark: "#101D33", navyLight: "#243860",
  gold: "#A8B8C8", goldLight: "#FFFFFF", goldPale: "#EEF3FA",
  white: "#FFFFFF", offWhite: "#EBF0F8", lightGray: "#D8E6F2",
  midGray: "#8A99AE", darkGray: "#3A4A60", textMuted: "#5A6880",
  border: "rgba(27,43,75,0.14)", dark1: "#071628", dark2: "#0d1e38",
};
const U = "'Urbanist', sans-serif";

// â”€â”€ GLOBAL RESPONSIVE STYLES â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const globalStyles = `
@import url('https://fonts.googleapis.com/css2?family=Urbanist:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,700&display=swap');
@keyframes spin { from{transform:rotate(0deg)}to{transform:rotate(360deg)} }

*, *::before, *::after { box-sizing: border-box; }

/* â”€â”€ HERO â”€â”€ */
.cst-hero {
  position: relative; width: 100%; height: 100vh;
  min-height: 580px; overflow: hidden;
  background: #060e1a; font-family: ${U};
}
.cst-vignette-base { position:absolute; inset:0; z-index:1; pointer-events:none; background:rgba(0,0,0,0.15); }
.cst-vignette {
  position:absolute; inset:0; z-index:1; pointer-events:none;
  background: radial-gradient(ellipse 55% 85% at 50% 48%,
    rgba(0,0,0,0) 0%, rgba(0,0,0,0.05) 40%,
    rgba(0,0,0,0.18) 68%, rgba(0,0,0,0.3) 85%, rgba(0,0,0,0.4) 100%);
}
.cst-vignette-bottom {
  position:absolute; bottom:0; left:0; right:0; height:25%;
  z-index:1; pointer-events:none;
  background: linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 100%);
}
.cst-vignette-left {
  position:absolute; top:0; left:0; bottom:0; width:55%;
  z-index:1; pointer-events:none;
  background: linear-gradient(to right, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 40%, transparent 100%);
}
.cst-vignette-top {
  position:absolute; top:0; left:0; right:0; height:25%;
  z-index:1; pointer-events:none;
  background: linear-gradient(to bottom, rgba(6,14,26,0.65) 0%, transparent 100%);
}
.cst-center {
  position:absolute; inset:0; z-index:2;
  display:flex; flex-direction:column;
  align-items:flex-start; justify-content:center;
  text-align:left; padding:68px clamp(48px,9vw,140px) 120px;
}
.cst-eyebrow {
  display:inline-flex; align-items:center; gap:10px; margin-bottom:24px;
  animation: cst-rise 0.8s ease both;
}
.cst-eyebrow-line { width:28px; height:1px; background:#FFFFFF; opacity:0.6; }
.cst-eyebrow span {
  font-size:10px; font-weight:700; letter-spacing:0.2em;
  text-transform:uppercase; color:#FFFFFF;
}
.cst-h1 {
  font-size:clamp(36px, 7.5vw, 100px); font-weight:900;
  line-height:0.98; letter-spacing:-0.035em; color:#fff;
  margin:0 0 18px; max-width:900px;
  animation: cst-rise 0.85s 0.1s ease both;
  text-shadow: 0 2px 20px rgba(0,0,0,0.6), 0 4px 60px rgba(0,0,0,0.4);
}
.cst-h1 em { font-style:normal; color:#FFFFFF; }
.cst-sub {
  font-size:clamp(14px, 1.6vw, 19px); font-weight:400;
  color:rgba(255,255,255,0.72); line-height:1.7;
  max-width:520px; margin:0 0 36px;
  animation: cst-rise 0.85s 0.2s ease both;
}
.cst-ctas {
  display:flex; align-items:center; gap:12px;
  flex-wrap:wrap; justify-content:flex-start; margin-bottom:32px;
  animation: cst-rise 0.85s 0.3s ease both;
}
.cst-btn-gold {
  font-family:${U}; font-size:13px; font-weight:800;
  letter-spacing:0.1em; text-transform:uppercase;
  color:#101D33; background:#A8B8C8; padding:14px 32px;
  border-radius:4px; text-decoration:none;
  box-shadow:0 6px 28px rgba(168,184,200,0.4);
  transition:background 0.2s, transform 0.15s;
}
.cst-btn-gold:hover { background:#FFFFFF; transform:translateY(-2px); }
.cst-btn-ghost {
  font-family:${U}; font-size:13px; font-weight:600;
  color:rgba(255,255,255,0.8); background:rgba(255,255,255,0.1);
  backdrop-filter:blur(10px); border:1px solid rgba(255,255,255,0.22);
  padding:14px 28px; border-radius:4px; text-decoration:none;
  transition:background 0.2s;
}
.cst-btn-ghost:hover { background:rgba(255,255,255,0.18); color:#fff; }
.cst-btn-outline {
  font-family:${U}; font-size:13px; font-weight:700;
  letter-spacing:0.1em; text-transform:uppercase;
  color:#fff; background:transparent;
  border:2px solid rgba(255,255,255,0.65);
  padding:14px 36px; border-radius:3px; text-decoration:none;
  display:inline-flex; align-items:center; gap:8px;
  transition:border-color 0.2s, color 0.2s, background 0.2s;
}
.cst-btn-outline:hover { border-color:#FFFFFF; color:#FFFFFF; background:rgba(255,255,255,0.07); }
.cst-hero-features {
  position:absolute; bottom:0; left:0; right:0; z-index:2;
  display:flex; border-top:1px solid rgba(255,255,255,0.15);
  background:rgba(0,0,0,0.28); backdrop-filter:blur(6px);
  -webkit-backdrop-filter:blur(6px);
}
.cst-hero-feature-item {
  flex:1; display:flex; align-items:center; justify-content:space-between; gap:12px;
  padding:20px clamp(20px,3vw,48px);
  border-right:1px solid rgba(255,255,255,0.12);
  font-family:${U}; font-size:13px; font-weight:500;
  color:rgba(255,255,255,0.9); letter-spacing:0.01em; line-height:1.4;
}
.cst-hero-feature-item:last-child { border-right:none; }
@keyframes cst-rise {
  from { opacity:0; transform:translateY(18px); }
  to   { opacity:1; transform:translateY(0); }
}

/* â”€â”€ RESPONSIVE UTILITY CLASSES â”€â”€ */
.hp-section        { padding:80px 24px; }
.hp-section-dark   { padding:80px 24px; }
.hp-inner          { max-width:1200px; margin:0 auto; }
.hp-inner-sm       { max-width:860px; margin:0 auto; }
.hp-inner-xs       { max-width:800px; margin:0 auto; }

/* 2-col grids that collapse to 1 on mobile */
.hp-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:start; }
.hp-grid-2-eq { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.hp-grid-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; }
.hp-grid-programs { display:grid; grid-template-columns:repeat(auto-fit,minmax(280px,1fr)); gap:24px; }
.hp-grid-features { display:grid; grid-template-columns:1fr 1fr; gap:20px; }
.hp-grid-team     { display:grid; grid-template-columns:repeat(auto-fit,minmax(240px,1fr)); gap:24px; }
.hp-grid-support  { display:grid; grid-template-columns:repeat(4,1fr); gap:20px; }
.hp-grid-testi    { display:grid; grid-template-columns:repeat(auto-fit,minmax(280px,1fr)); gap:24px; margin-bottom:80px; }
.hp-grid-blog     { display:grid; grid-template-columns:repeat(auto-fit,minmax(280px,1fr)); gap:24px; }
.hp-form-2col     { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-bottom:20px; }

/* blog header row */
.hp-blog-header { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:60px; }

/* road-ready promo grid */
.hp-manual-grid { display:grid; grid-template-columns:1fr 1fr; gap:clamp(40px,6vw,80px); align-items:center; }

/* calc section */
.hp-calc-grid { display:grid; grid-template-columns:1fr 1.2fr; gap:80px; align-items:center; }

/* â”€â”€ MOBILE â‰¤768px â”€â”€ */
@media (max-width: 768px) {
  .cst-hero { height: 100vh !important; min-height: 0 !important; }
  .cst-center {
    position: absolute; inset: 0; z-index: 2;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center;
    padding: 80px 20px 80px;
  }
  .cst-ctas { justify-content:center; }
  .cst-vignette-left { display:none; }
  .cst-hero-features { flex-direction:column; }
  .cst-hero-feature-item {
    border-right:none; border-bottom:1px solid rgba(255,255,255,0.07);
    padding:10px 16px; font-size:12px; gap:10px;
    flex-direction:row; justify-content:space-between; align-items:center;
  }
  .cst-hero-feature-item:last-child { border-bottom:none; }
  .cst-hero-feature-item svg { flex-shrink:0; width:20px; height:20px; }
  .cst-h1 { font-size:clamp(28px,8.5vw,44px); margin-bottom:10px; line-height:1.02; }
  .cst-sub { font-size:13.5px; margin-bottom:18px; max-width:100%; }
  .cst-ctas { flex-direction:column; width:100%; gap:9px; margin-bottom:16px; }
  .cst-btn-gold, .cst-btn-ghost { width:100%; text-align:center; justify-content:center; padding:13px 20px; font-size:12px; }
  .cst-eyebrow { margin-bottom:12px; }
  .cst-eyebrow span { font-size:9px; letter-spacing:0.14em; }
  .cst-dots { margin-top:0; margin-bottom: 0; }

  /* Vignettes reduced on mobile */
  .cst-vignette-bottom { height: 20%; }
  .cst-vignette-top    { height: 15%; }

  /* Sections */
  .hp-section, .hp-section-dark { padding:60px 20px; }

  /* Grids â†’ single column */
  .hp-grid-2        { grid-template-columns:1fr; gap:40px; }
  .hp-grid-2-eq     { grid-template-columns:1fr; }
  .hp-grid-3        { grid-template-columns:1fr; }
  .hp-grid-features { grid-template-columns:1fr; }
  .hp-grid-support  { grid-template-columns:1fr 1fr; }
  .hp-form-2col     { grid-template-columns:1fr; }
  .hp-calc-grid     { grid-template-columns:1fr; gap:40px; }

  /* Blog header stacks */
  .hp-blog-header   { flex-direction:column; align-items:flex-start; gap:16px; margin-bottom:36px; }
}

/* â”€â”€ TABLET 769–1024px â”€â”€ */
@media (min-width:769px) and (max-width:1024px) {
  .hp-section, .hp-section-dark { padding:70px 32px; }
  .hp-grid-support  { grid-template-columns:repeat(2,1fr); }
  .hp-grid-3        { grid-template-columns:1fr 1fr; }
  .hp-calc-grid     { grid-template-columns:1fr; gap:48px; }
  .hp-grid-2        { gap:48px; }
}

/* â”€â”€ ABOUT SECTION â”€â”€ */
.cst-about-top   { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; margin-bottom:72px; }
.cst-about-mid   { display:grid; grid-template-columns:1fr auto 1fr; align-items:center; }
.cst-about-col   { display:flex; flex-direction:column; gap:36px; }
.cst-about-stats { display:grid; grid-template-columns:repeat(6,1fr); }
@media (max-width:768px) {
  .cst-about-top  { grid-template-columns:1fr; gap:28px; margin-bottom:40px; }
  .cst-about-mid  { grid-template-columns:1fr; gap:16px; }
  .cst-about-img  { display:none; }
  .cst-about-col  { gap:16px; padding-left:0 !important; padding-right:0 !important; }
  .cst-about-col > div { flex-direction:row !important; }
  .cst-about-col > div > div:last-child:not([style*="border"]) { order:-1; }
  .cst-about-stats { grid-template-columns:1fr 1fr 1fr; }
  .cst-about-stat { border-right:none !important; border-bottom:1px solid rgba(255,255,255,0.06) !important; padding:28px 12px !important; }
  .cst-about-stat:nth-child(3n) { border-right:none !important; }
  .cst-about-stat:nth-child(3n+1), .cst-about-stat:nth-child(3n+2) { border-right:1px solid rgba(255,255,255,0.06) !important; }
  .cst-about-stat:nth-last-child(-n+3) { border-bottom:none !important; }
}

/* â”€â”€ MOBILE-SPECIFIC FIXES â”€â”€ */
@media (max-width: 480px) {
  .hp-grid-support { grid-template-columns:1fr; }
  .cst-about-stats { grid-template-columns:1fr 1fr; }
  .cst-about-stat:nth-child(3n+1), .cst-about-stat:nth-child(3n+2) { border-right:none !important; }
  .cst-about-stat:nth-child(odd) { border-right:1px solid rgba(255,255,255,0.06) !important; }
  .cst-about-stat:nth-last-child(-n+2) { border-bottom:none !important; }
}
@media (min-width:769px) and (max-width:1024px) {
  .cst-about-mid  { grid-template-columns:1fr auto 1fr; }
  .cst-about-img  { width:220px !important; height:220px !important; }
}
`;

// â”€â”€ DATA â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const SLIDES = [
  { url: facilityImg15 }, // facility image 15
  { url: "https://images.unsplash.com/photo-1592805144716-feeccccef5ac?w=1800&q=90" }, // red American semi truck on road
  { url: "https://images.unsplash.com/photo-1720811559395-3ed8d1b16649?w=1800&q=90" }, // red semi truck country road
  { url: "https://images.unsplash.com/photo-1591768793355-74d04bb6608f?w=1800&q=90" }, // white & blue semi truck on road
  { url: "https://images.unsplash.com/photo-1592838064575-70ed626d3a0e?w=1800&q=90" }, // black semi truck on highway
];
const HERO_FEATURES = [
  { icon: Truck,       label: "Class A & Class B CDL Training"  },
  { icon: TrendingUp,  label: "96% Graduate Job Placement Rate" },
  { icon: Shield,      label: "VA & GI Bill Approved Program"   },
];
const programs = [
  { icon: Truck,    title: "Class A CDL",       subtitle: "Commercial Driver License", desc: "Full tractor-trailer training covering pre-trip inspection, backing maneuvers, highway driving, and DOT compliance.",         duration: "10 Weeks", badge: "Most Popular" },
  { icon: Shield,   title: "Class B CDL",        subtitle: "Single-Unit Vehicle",      desc: "Comprehensive training for straight trucks, passenger buses, and large single-unit commercial vehicles.",                      duration: "8 Weeks",  badge: null          },
  { icon: FileText, title: "Hazmat Endorsement", subtitle: "Hazardous Materials",      desc: "Federal certification training for transporting hazardous materials with full regulatory compliance preparation.",             duration: "1 Week",   badge: null          },
];
const features = [
  { icon: BookOpen,       title: "ELDT-Compliant Curriculum", desc: "Federally registered Entry-Level Driver Training meeting all federal requirements."       },
  { icon: Users,          title: "Experienced Instructors",   desc: "CDL-certified instructors with professional driving and teaching experience."              },
  { icon: TrendingUp,     title: "Career Placement",          desc: "Dedicated career services connecting graduates with top carriers nationwide."              },
  { icon: DollarSign,     title: "Financial Assistance",      desc: "Multiple financing options covering up to 100% of tuition for qualifying students."        },

  { icon: HeartHandshake, title: "Veterans Benefits",         desc: "Approved for Post 9/11 GI Bill and VA education benefits."                                 },
];
const blogs = [
  { category: "Career Guidance", title: "How to Choose the Right CDL Program for Your Career Goals",      excerpt: "Understanding the differences between Class A and Class B licensing and which path aligns best with your professional aspirations.", date: "March 18, 2026" },
  { category: "Industry News",   title: "Federal ELDT Regulations: What Every Aspiring Driver Must Know", excerpt: "A comprehensive overview of the Entry-Level Driver Training requirements and how they affect new CDL applicants in California.",        date: "March 5, 2026"  },
  { category: "Veterans",        title: "Maximizing Your GI Bill Benefits for Commercial Driver Training", excerpt: "A step-by-step guide for veterans looking to use their education benefits to launch a high-paying trucking career.",                  date: "February 20, 2026" },
];
const faqs = [
  { q: "What are the requirements to enroll?",         a: "Applicants must be at least 18 years old (21 for interstate driving), hold a valid driver's license, pass a DOT physical, and meet eligibility criteria for their state." },
  { q: "How long does the CDL training program take?", a: "Our Class A CDL program is completed in 10 weeks, and our Class B CDL program in 8 weeks — covering all required classroom instruction and behind-the-wheel training hours." },
  { q: "Do you offer financial assistance?",           a: "Yes. We offer multiple financing options including in-house financing, carrier-sponsored tuition reimbursement, and acceptance of the Post 9/11 GI Bill for qualifying veterans." },
  { q: "Will I have a job after graduation?",          a: "Our Career Services team works actively with partner carriers to connect graduates with employment opportunities. Many students receive job offers before completing the program." },
];
const team = [
  { name: "Otis Lee Cooper Jr.", title: "Founder & CEO · Road Ready Program Creator", exp: '"Bounty Hunter Trucker" · 34-Year CDL Holder', bio: `Otis Lee Cooper Jr. — known throughout the transportation industry as "Bounty Hunter Trucker" — is a third-generation trucking professional, legal consultant, actor, instructor, investigator, entrepreneur, media personality, and transportation advocate with more than three decades of real-world experience spanning transportation, compliance, safety, investigations, media, and business development.

Born in Pinal County, Arizona, Cooper has built a reputation around one core philosophy: Responsibility • Accountability • Consequences. That philosophy is the foundation of his training systems, legal consulting work, media platforms, investigative services, and public advocacy throughout the trucking and transportation industries.

Cooper obtained his commercial driving license at 18 years old operating a twin-stick transmission truck, building his foundation in traditional hands-on trucking before expanding into high-value freight, movie and trade show transportation, agriculture, flatbed, hopper, tanker, refrigeration unit operations, and military-related freight support.

A proud Native American, Cooper is a former congressional candidate for California's 25th District, an FMCSA-certified transportation professional, a 34-year CDL holder, longtime instructor, martial arts instructor, and public speaker committed to raising standards within the trucking industry.

He is the founder and creator of the Road Ready Program for the California School of Trucking — an advanced driver training concept designed to take students far beyond basic CDL instruction. The program focuses on real-world preparedness, defensive driving, mountain driving, brake adjustments, FMCSA compliance, cargo securement, snow chains, inspections, and professional driver responsibility.

Cooper is recognized as a writer, contributor, and media personality featured in Truck Club Magazine, El Troquero Newspaper, SiriusXM Radio, transportation podcasts, and news media platforms. He has appeared on Fox 11 News, KTLA 5 News, City Watch LA, and in national publications including Home Defender Magazine and The Banksters: Madoff with America. He is the original Cooper of Cooper's Crusaders reality show, and has appeared on the Teen 911 Reality Show and in the Slumlords documentary.

PROFESSIONAL ROLES & BACKGROUND
• Legal Consultant · 20-Year Legal Investigator
• Actor & Reality Television Personality
• 21-Year Fugitive Recovery Instructor
• 35-Year Martial Arts Practitioner & Instructor
• Senior Investigator / Volunteer — Missing Children Investigation Agency
• Public Speaker, Bodyguard, Mediator & Content Creator
• Transportation Advocate, Business Strategist & Radio Guest Personality

KNOWN BRANDS & PROJECTS
• California School of Trucking — Creator of the Road Ready Program
• TRAC Podcast
• LEGAL Defense Investigations & Mediation Services
• America's Top Trucker
• Founder — US Enforcement Agency Online Magazine
• Co-Inventor of MegaBeam Flashlight
• Founder — Bring It to Reality Video Investigations
• Supporter of SafeKidZone.com

LEGAL & INVESTIGATIVE FOCUS
• Civil, family, and criminal case support
• Investigative research and evidence review
• Transportation compliance and driver accountability
• Public safety advocacy and child protection support

"Ignorance is expensive."`, photo: otisPhoto, initials: "OC", accent: C.gold },
  { name: "Mr. Andrew Cooper", title: "Chief Operating Officer (COO)",                         exp: "Operations & Training",          bio: "Andrew Cooper was born and raised in Southern California as a fourth generation truck driver and the grandson of a decorated Vietnam Veteran. Growing up with deep roots in the transportation industry, Andrew has been raised to appreciate and respect not only the skills but the struggles that drivers face in the country. At the age of 21, Andrew left his career as a mechanic and decided to join the family business. With over 6 years as a cross country owner operator, he has been able to take the lessons passed down to him from his family and his knowledge as a mechanic to gain not only the experience necessary to understand the ins and outs of the industry, but, the perspective of a modern driver with traditional values. He has spent a lifetime on the road with his father and grandfather and believes his training began as a boy watching the experts in action. He often says that trucking isn’t just a career but a way of life. With multiple members of his family being veterans, he feels the responsibility to utilize his knowledge to assist in empowering veterans where others fail to do so.", photo: andrewPhoto, initials: "AC", accent: C.goldLight },
  { name: "Mr. Randy Harden",  title: "Director of Veteran Affairs · VP of Veteran Advocacy", exp: "Veteran Advocacy",               bio: "Native to South Central Los Angeles, served 6 years in US Army. After service, first real opportunity for a career was training and ultimately operating as a professional driver in the Trucking Industry. Career in trucking spanned across 2 decades and ranged from driver to ownership. With my background and education in business, operations and logistics, I have a mission and vision to empower veterans and anyone else seeking opportunities to enter a career that offers endless growth.", photo: hardenPhoto, initials: "RH", accent: "#B22234"  },
  { name: "Ms. Arlean Garcia", title: "Executive Administrator · Secretary of the Corporation", exp: "Administration & Operations", bio: `Raised in a family deeply connected to the trucking industry, I developed an early appreciation for the hardworking drivers who keep our communities and economy moving, despite the many challenges and lack of recognition they often face. In my upbringing, I enjoyed attending trucking trade shows and was surrounded by several family members who are truck drivers, both local and interstate.

My experience began working in the office where my father was employed, where I gained knowledge in dispatching, check processing, and port logistics. Along with my background in the defense industry, I bring strong administrative and organizational experience to my role. Today, I serve in an executive administrative role and as Secretary of the Corporation, supporting company operations and growth while helping students begin successful careers in trucking. I am committed to providing opportunities for everyone, especially the veteran workforce, by helping create meaningful pathways within the industry.`, photo: arleanPhoto, initials: "AG", accent: "#5E97C2" },
];
const supportTeam = [
  { name: "Armeda Basco",     title: "Position to Be Announced",     initials: "AB", accent: C.gold,   photo: armedaPhoto, bio: "A valued member of the CST family whose role will be announced shortly."                                           },
  { name: "Irish Francisco",  title: "Marketing",                    initials: "IF", accent: "#7F77DD", photo: irishPhoto,  bio: "Drives CST's brand awareness and outreach efforts to connect students with career opportunities."                   },
  { name: "Johannes Angeles", title: "Full Stack Web Developer",     initials: "JA", accent: "#378ADD", photo: null,        bio: "Engineers CST's full web platform from database to UI, powering the school's digital infrastructure."              },
];
const ABOUT_FEATURES = [
  'Fast-track CDL programs completed in as little as 8–10 weeks — the fastest path to your commercial license in California.',
  'Late-model Freightliner tractor-trailer fleet at every campus for real-world, professional-grade skill building.',
  'Federally registered ELDT provider — one of the first in California to meet the 2022 FMCSA Entry-Level Driver Training mandate.',
  'VA-approved school accepting Post-9/11 GI Bill, WIOA grants, and employer-sponsored tuition reimbursement programs.',
];
const ABOUT_STATS = [
  { value: '34+',            label: 'Years of Experience'    },
  { value: '96%',            label: 'Job Placement Rate'     },
  { value: '$55K+',          label: 'Avg. Starting Salary'   },
  { value: 'State Licensed', label: '& DMV Certified'        },
  { value: 'VA Approved',    label: 'GI Bill Accepted'       },
  { value: 'ELDT',           label: 'Registered Provider'    },
];
const EMPTY_FORM = { name: "", email: "", course: "", location: "", rating: 0, recommend: null, comment: "" };
const API_BASE = process.env.REACT_APP_API_URL || "";

// â”€â”€ HELPERS â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function SectionLabel({ text, center = false }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"14px", justifyContent: center ? "center" : "flex-start" }}>
      <div style={{ width:"32px", height:"2px", background:C.gold }} />
      <span style={{ fontFamily:U, fontSize:"12px", fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", color:C.gold }}>{text}</span>
      {center && <div style={{ width:"32px", height:"2px", background:C.gold }} />}
    </div>
  );
}
function StarRating({ count }) {
  return (
    <div style={{ display:"flex", gap:"3px", marginBottom:"12px" }}>
      {Array.from({ length: count }).map((_, i) => <Star key={i} size={14} fill={C.gold} color={C.gold} />)}
    </div>
  );
}

// â”€â”€ HERO â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function CSTHero() {
  const audioRef              = useRef(null);
  const [muted,   setMuted]   = useState(true);   // starts muted until user interacts
  const [volume,  setVolume]  = useState(0.2);     // 20% default
  const [started, setStarted] = useState(false);   // tracks first user interaction

  // Sync volume + muted state to the audio element
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = muted ? 0 : volume;
  }, [volume, muted]);

  const handleUnmute = () => {
    const a = audioRef.current;
    if (!a) return;
    if (!started) {
      a.play().catch(() => {});
      setStarted(true);
    }
    setMuted(false);
  };

  const handleMute = () => setMuted(true);

  const handleVolume = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (!started) {
      audioRef.current?.play().catch(() => {});
      setStarted(true);
    }
    setMuted(v === 0);
  };

  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  return (
    <section className="cst-hero">
      {/* Background audio — starts muted, user controls volume */}
      <audio ref={audioRef} src={heroTrack} loop preload="none" />

      {/* Background video */}
      <video
        autoPlay muted loop playsInline
        style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', zIndex:0 }}
      >
        <source src={heroVideo} type="video/mp4" />
      </video>

      {/* Overlays */}
      <div className="cst-vignette-base" />
      <div className="cst-vignette-top" />
      <div className="cst-vignette-left" />
      <div className="cst-vignette" />
      <div className="cst-vignette-bottom" />

      {/* Center content */}
      <div className="cst-center">
        <h1 className="cst-h1">Professional<br />CDL Training<br />in <em>California.</em></h1>
        <p className="cst-sub">Federally registered, hands-on commercial driver training with career placement support from day one.</p>
        <div className="cst-ctas">
          <a href="/apply" className="cst-btn-outline">Enroll Now <ChevronRight size={16} /></a>
        </div>
      </div>

      {/* â”€â”€ Floating volume control â”€â”€ */}
      <div style={{
        position: 'absolute', bottom: '90px', right: '24px', zIndex: 10,
        display: 'flex', alignItems: 'center', gap: '10px',
        background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '40px', padding: '8px 16px 8px 12px',
      }}>
        {/* Mute / unmute toggle */}
        <button
          onClick={muted ? handleUnmute : handleMute}
          title={muted ? 'Play music' : 'Mute music'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', color: '#fff' }}
        >
          <VolumeIcon size={18} color={muted ? 'rgba(255,255,255,0.45)' : '#A8B8C8'} />
        </button>

        {/* Volume slider */}
        <input
          type="range"
          min="0" max="1" step="0.01"
          value={muted ? 0 : volume}
          onChange={handleVolume}
          style={{
            width: '80px', accentColor: '#A8B8C8', cursor: 'pointer',
            height: '3px', appearance: 'auto',
          }}
        />

        {/* Label */}
        <span style={{
          fontFamily: U, fontSize: '10px', fontWeight: 600,
          color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em',
          textTransform: 'uppercase', whiteSpace: 'nowrap',
        }}>
          {muted ? 'Music off' : 'CST Radio'}
        </span>
      </div>

      {/* Feature bar */}
      <div className="cst-hero-features">
        {HERO_FEATURES.map((f, i) => {
          const Icon = f.icon;
          return (
            <div key={i} className="cst-hero-feature-item">
              <span>{f.label}</span>
              <Icon size={28} color="rgba(255,255,255,0.7)" style={{ flexShrink:0 }} />
            </div>
          );
        })}
      </div>
    </section>
  );
}

// â”€â”€ ABOUT SECTION â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function AboutSection() {
  return (
    <section style={{ background:'#021B3A', fontFamily:U, overflow:'hidden' }}>
      <div style={{ maxWidth:'1200px', margin:'0 auto', padding:'80px clamp(20px,5vw,64px) 0' }}>

        {/* Top: title + description */}
        <div className="cst-about-top">
          <div style={{ position:'relative' }}>
            <div style={{
              position:'absolute', top:'-10px', left:'-5px',
              fontSize:'clamp(80px,14vw,160px)', fontWeight:900,
              color:'rgba(255,255,255,0.03)', lineHeight:1,
              userSelect:'none', letterSpacing:'-0.05em', pointerEvents:'none',
            }}>CST</div>
            <div style={{ position:'relative', zIndex:1 }}>
              <div style={{ fontSize:'11px', fontWeight:600, letterSpacing:'0.18em', textTransform:'uppercase', color:'#757575', marginBottom:'10px' }}>Trucking School</div>
              <h2 style={{ fontFamily:U, fontSize:'clamp(26px,4vw,46px)', fontWeight:900, color:'#fff', lineHeight:1.1, margin:0 }}>
                California School<br />of Trucking
              </h2>
            </div>
          </div>
          <p style={{ fontFamily:U, fontSize:'clamp(14px,1.1vw,15px)', color:'#757575', lineHeight:1.9, margin:0 }}>
            California School of Trucking is led by professionals with over 34 years of industry experience — bringing real-world knowledge, professional standards, and genuine commitment to every student's career. We have trained thousands of drivers, built lasting carrier partnerships, and maintained a 96% graduate job placement rate. Our federally registered ELDT programs are designed to get you licensed, skilled, and career-ready in weeks, not years.
          </p>
        </div>

        {/* Middle: left callouts | center image | right callouts */}
        <div className="cst-about-mid">
          {/* Left */}
          <div className="cst-about-col" style={{ paddingRight:'clamp(16px,3vw,44px)' }}>
            {ABOUT_FEATURES.slice(0,2).map((text, i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'14px' }}>
                <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'8px', padding:'18px 22px', flex:1 }}>
                  <p style={{ fontFamily:U, fontSize:'13px', color:'rgba(255,255,255,0.8)', lineHeight:1.75, margin:0 }}>{text}</p>
                </div>
                <div style={{ width:'12px', height:'12px', borderRadius:'50%', background:'#A8B8C8', flexShrink:0, marginTop:'22px' }} />
              </div>
            ))}
          </div>

          {/* Center image */}
          <div className="cst-about-img" style={{
            width:'clamp(240px,20vw,320px)', height:'clamp(240px,20vw,320px)',
            borderRadius:'50%', overflow:'hidden', flexShrink:0,
            border:'3px solid rgba(168,184,200,0.3)',
            boxShadow:'0 0 60px rgba(168,184,200,0.1)',
          }}>
            <img
              src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80"
              alt="Modern Electric Class 8 Truck"
              style={{ width:'100%', height:'100%', objectFit:'cover' }}
            />
          </div>

          {/* Right */}
          <div className="cst-about-col" style={{ paddingLeft:'clamp(16px,3vw,44px)' }}>
            {ABOUT_FEATURES.slice(2,4).map((text, i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'14px' }}>
                <div style={{ width:'12px', height:'12px', borderRadius:'50%', background:'#A8B8C8', flexShrink:0, marginTop:'22px' }} />
                <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'8px', padding:'18px 22px', flex:1 }}>
                  <p style={{ fontFamily:U, fontSize:'13px', color:'rgba(255,255,255,0.8)', lineHeight:1.75, margin:0 }}>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ marginTop:'80px', borderTop:'1px solid rgba(255,255,255,0.06)', background:'rgba(0,0,0,0.2)' }}>
        <div style={{ maxWidth:'1200px', margin:'0 auto', padding:'0 clamp(20px,5vw,64px)' }}>
          <div className="cst-about-stats">
            {ABOUT_STATS.map((s, i) => (
              <div key={i} className="cst-about-stat" style={{
                padding:'48px 24px', textAlign:'center',
                borderRight: i < 5 ? '1px solid rgba(255,255,255,0.06)' : 'none',
              }}>
                <div style={{ fontFamily:U, fontSize: /^\d/.test(s.value) ? 'clamp(36px,5vw,60px)' : 'clamp(15px,1.6vw,20px)', fontWeight:900, color:'#fff', letterSpacing:'-0.02em', lineHeight:1.2, marginBottom:'10px' }}>{s.value}</div>
                <div style={{ fontFamily:U, fontSize:'13px', color:'#757575', letterSpacing:'0.02em', lineHeight:1.5 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// â”€â”€ TESTIMONIALS â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [fetchError,   setFetchError]   = useState(null);
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [errors,       setErrors]       = useState({});
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState(null);
  const [submitted,    setSubmitted]    = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/testimonials`);
        const json = await res.json();
        if (!json.success) throw new Error(json.message || "Failed");
        setTestimonials(json.data);
      } catch { setFetchError("Could not load testimonials. Please try again later."); }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (f, v) => { setForm(x => ({ ...x, [f]: v })); setErrors(e => ({ ...e, [f]: "" })); };
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "A valid email is required.";
    if (!form.rating) e.rating = "Please select a star rating.";
    if (form.recommend === null) e.recommend = "Please choose an option.";
    if (!form.comment.trim() || form.comment.trim().length < 20) e.comment = "Please write at least 20 characters.";
    setErrors(e); return Object.keys(e).length === 0;
  };
  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/api/testimonials`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ name:form.name.trim(), email:form.email.trim(), course:form.course, location:form.location.trim(), rating:form.rating, recommend:form.recommend, comment:form.comment.trim() }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Submission failed");
      setSubmitted(true);
    } catch (err) { setSubmitError(err.message || "Something went wrong."); }
    finally { setSubmitting(false); }
  };

  const fieldLabel = (text, optional = false) => (
    <span style={{ fontFamily:U, fontSize:"11px", fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase", color:"rgba(255,255,255,0.45)", marginBottom:"7px", display:"block" }}>
      {text}{optional && <span style={{ textTransform:"none", letterSpacing:0, fontWeight:300, color:"rgba(255,255,255,0.25)", marginLeft:"6px" }}>(optional)</span>}
    </span>
  );
  const inp = { width:"100%", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(168,184,200,0.25)", borderRadius:"4px", padding:"10px 14px", fontSize:"14px", color:C.white, fontFamily:U, outline:"none", boxSizing:"border-box" };
  const err = { fontFamily:U, fontSize:"12px", color:"#F09595", marginTop:"5px", display:"block" };

  return (
    <section className="hp-section-dark" style={{ background:C.dark2 }}>
      <div className="hp-inner">
        <div style={{ marginBottom:"48px" }}>
          <SectionLabel text="Graduate Testimonials" />
          <h2 style={{ fontFamily:U, fontSize:"clamp(26px,3.5vw,42px)", fontWeight:800, color:C.white, margin:0 }}>What Our Alumni Say</h2>
        </div>

        {loading ? (
          <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:"12px", padding:"60px 0", color:"rgba(255,255,255,0.4)" }}>
            <Loader2 size={20} style={{ animation:"spin 1s linear infinite" }} />
            <span style={{ fontFamily:U, fontSize:"14px" }}>Loading testimonials...</span>
          </div>
        ) : fetchError ? (
          <p style={{ fontFamily:U, fontSize:"14px", color:"#F09595", textAlign:"center", padding:"40px 0" }}>{fetchError}</p>
        ) : testimonials.length === 0 ? (
          <p style={{ fontFamily:U, fontSize:"14px", color:"rgba(255,255,255,0.3)", textAlign:"center", padding:"40px 0" }}>No testimonials yet. Be the first to leave a review below.</p>
        ) : (
          <div className="hp-grid-testi">
            {testimonials.map(t => (
              <div key={t._id} style={{ background:C.dark1, border:"1px solid rgba(168,184,200,0.15)", borderRadius:"4px", padding:"28px" }}>
                <StarRating count={t.rating} />
                <p style={{ fontFamily:U, fontSize:"15px", color:"rgba(255,255,255,0.8)", lineHeight:1.75, margin:"0 0 24px", fontStyle:"italic" }}>"{t.comment}"</p>
                <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:"16px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"10px" }}>
                  <div>
                    <div style={{ fontFamily:U, fontSize:"14px", fontWeight:600, color:C.white }}>{t.name}</div>
                    {t.location && <div style={{ fontFamily:U, fontSize:"12px", color:"rgba(255,255,255,0.45)", display:"flex", alignItems:"center", gap:"4px", marginTop:"2px" }}><MapPin size={11}/> {t.location}</div>}
                    <div style={{ display:"flex", alignItems:"center", gap:"5px", marginTop:"6px" }}>
                      {t.recommend ? <ThumbsUp size={11} color={C.gold}/> : <ThumbsDown size={11} color="rgba(255,255,255,0.3)"/>}
                      <span style={{ fontFamily:U, fontSize:"11px", color:t.recommend ? C.gold : "rgba(255,255,255,0.3)" }}>{t.recommend ? "Recommends" : "Does not recommend"}</span>
                    </div>
                  </div>
                  {t.course && <span style={{ fontFamily:U, fontSize:"11px", fontWeight:600, letterSpacing:"0.06em", textTransform:"uppercase", color:C.dark1, background:C.gold, padding:"4px 10px", borderRadius:"2px", whiteSpace:"nowrap" }}>{t.course}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ height:"1px", background:"rgba(168,184,200,0.2)", margin:"48px 0" }} />

        <div style={{ background:C.dark1, border:"1px solid rgba(168,184,200,0.2)", borderRadius:"6px", padding:"clamp(24px,4vw,48px)", maxWidth:"860px", margin:"0 auto" }}>
          {!submitted ? (
            <>
              <div style={{ marginBottom:"28px" }}>
                <SectionLabel text="Share Your Experience" />
                <h3 style={{ fontFamily:U, fontSize:"clamp(20px,2.5vw,28px)", fontWeight:800, color:C.goldLight, margin:"0 0 8px" }}>Leave a Review</h3>
                <p style={{ fontFamily:U, fontSize:"14px", fontWeight:300, color:"rgba(255,255,255,0.45)", lineHeight:1.7, margin:0 }}>Your feedback helps future students make confident decisions.</p>
              </div>
              <div className="hp-form-2col">
                <div>{fieldLabel("Full Name")}<input type="text" placeholder="e.g. Alex M." value={form.name} onChange={e=>set("name",e.target.value)} style={{...inp,borderColor:errors.name?"#F09595":"rgba(168,184,200,0.25)"}}/>{errors.name&&<span style={err}>{errors.name}</span>}</div>
                <div>{fieldLabel("Email Address")}<input type="email" placeholder="you@email.com" value={form.email} onChange={e=>set("email",e.target.value)} style={{...inp,borderColor:errors.email?"#F09595":"rgba(168,184,200,0.25)"}}/>{errors.email&&<span style={err}>{errors.email}</span>}</div>
              </div>
              <div className="hp-form-2col">
                <div>{fieldLabel("Course Attended",true)}<select value={form.course} onChange={e=>set("course",e.target.value)} style={inp}><option value="">Select a program...</option><option>Class A CDL</option><option>Class B CDL</option><option>Hazmat Endorsement</option><option>Other</option></select></div>
                <div>{fieldLabel("Your Location",true)}<input type="text" placeholder="City, State" value={form.location} onChange={e=>set("location",e.target.value)} style={inp}/></div>
              </div>
              <div className="hp-form-2col">
                <div>{fieldLabel("Star Rating")}<div style={{display:"flex",gap:"6px",paddingTop:"4px"}}>{[1,2,3,4,5].map(n=>(<button key={n} onClick={()=>set("rating",n)} style={{background:"none",border:"none",cursor:"pointer",padding:"2px",fontSize:"26px",lineHeight:1,color:n<=form.rating?C.gold:"rgba(255,255,255,0.15)",transform:n<=form.rating?"scale(1.1)":"scale(1)",transition:"color 0.15s,transform 0.15s"}}>â˜…</button>))}</div>{errors.rating&&<span style={err}>{errors.rating}</span>}</div>
                <div>{fieldLabel("Would You Recommend Us?")}<div style={{display:"flex",gap:"10px",paddingTop:"4px"}}>{[{val:true,label:"Yes",Icon:ThumbsUp},{val:false,label:"No",Icon:ThumbsDown}].map(({val,label,Icon})=>{const active=form.recommend===val;return(<button key={String(val)} onClick={()=>set("recommend",val)} style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:"7px",padding:"10px 12px",background:active?"rgba(168,184,200,0.15)":"rgba(255,255,255,0.04)",border:`1px solid ${active?C.gold:"rgba(168,184,200,0.2)"}`,borderRadius:"4px",cursor:"pointer",fontFamily:U,fontSize:"13px",fontWeight:active?600:400,color:active?C.goldLight:"rgba(255,255,255,0.45)",transition:"all 0.15s"}}><Icon size={14}/>{label}</button>);})}</div>{errors.recommend&&<span style={err}>{errors.recommend}</span>}</div>
              </div>
              <div style={{marginBottom:"24px"}}>{fieldLabel("Your Comment")}<textarea rows={5} placeholder="Tell us about your experience..." value={form.comment} onChange={e=>set("comment",e.target.value)} style={{...inp,resize:"vertical",borderColor:errors.comment?"#F09595":"rgba(168,184,200,0.25)",lineHeight:1.65}}/>{errors.comment&&<span style={err}>{errors.comment}</span>}</div>
              {submitError&&<p style={{fontFamily:U,fontSize:"13px",color:"#F09595",marginBottom:"16px"}}>{submitError}</p>}
              <button onClick={handleSubmit} disabled={submitting} style={{display:"inline-flex",alignItems:"center",gap:"8px",fontFamily:U,fontSize:"13px",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:C.dark1,background:submitting?C.midGray:C.gold,padding:"14px 32px",borderRadius:"3px",border:"none",cursor:submitting?"not-allowed":"pointer",width:"100%",justifyContent:"center",transition:"background 0.15s"}}>
                {submitting?<><Loader2 size={14} style={{animation:"spin 1s linear infinite"}}/> Submitting...</>:<><Send size={14}/> Submit My Review</>}
              </button>
              <p style={{fontFamily:U,fontSize:"12px",color:"rgba(255,255,255,0.25)",marginTop:"12px",lineHeight:1.6}}>Your email is used for verification only and will never be shared.</p>
            </>
          ) : (
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontSize:"48px",marginBottom:"16px"}}>âœ…</div>
              <h3 style={{fontFamily:U,fontSize:"24px",fontWeight:800,color:C.goldLight,margin:"0 0 12px"}}>Thank You for Your Review!</h3>
              <p style={{fontFamily:U,fontSize:"15px",fontWeight:300,color:"rgba(255,255,255,0.5)",lineHeight:1.75,maxWidth:"400px",margin:"0 auto"}}>Your submission has been received and will be published shortly.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// â”€â”€ MAIN PAGE â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export default function HomePage() {
  const [open, setOpen]               = useState(null);
  const [showTeam, setShowTeam]       = useState(false);
  const [bioModal, setBioModal]       = useState(null);
  const [newsArticles, setNewsArticles] = useState([]);
  const [newsLoading, setNewsLoading]   = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/news?page=1&pageSize=3`)
      .then(r => r.json())
      .then(d => { if (d.articles) setNewsArticles(d.articles.slice(0, 3)); })
      .catch(() => {})
      .finally(() => setNewsLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>California School of Trucking | CDL Training in California</title>
        <meta name="description" content="Get your CDL in as little as 10 weeks. ELDT-compliant, VA-approved CDL school in California. Class A & B programs. Call +1 (818) 438-3604." />
        <meta property="og:title" content="California School of Trucking | CDL Training in California" />
        <meta property="og:description" content="Get your CDL in as little as 10 weeks. ELDT-compliant, VA-approved CDL school in California. Class A & B programs. Call +1 (818) 438-3604." />
        <meta property="og:type" content="website" />
      </Helmet>
      <style>{globalStyles}</style>

      {/* â”€â”€ HERO â”€â”€ */}
      <CSTHero />

      {/* â”€â”€ AD â”€â”€ */}
      <div style={{ display:"flex", justifyContent:"center", padding:"20px 16px 0", overflowX:"hidden", background:"#021B3A" }}>
        <AdSlot placement="homepage_banner" width={728} height={90} />
      </div>

      {/* â”€â”€ ABOUT â”€â”€ */}
      <AboutSection />

      {/* â”€â”€ OUR MISSION â”€â”€ */}
      <section className="hp-section" style={{ background:"#EEF3FA" }}>
        <div className="hp-inner">
          <div style={{ textAlign:"center", marginBottom:"20px" }}>
            <span style={{ fontFamily:U, fontSize:"12px", fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", color:C.navy, border:`1px solid rgba(27,43,75,0.25)`, borderRadius:"20px", padding:"5px 16px" }}>Our Mission</span>
          </div>
          <h2 style={{ fontFamily:U, fontSize:"clamp(28px,5vw,56px)", fontWeight:900, color:C.navy, textAlign:"center", lineHeight:1.1, margin:"0 0 24px" }}>
            We Don't Just Train Drivers.<br />We Launch Careers.
          </h2>
          <div style={{ maxWidth:"600px", margin:"0 auto 48px", textAlign:"center" }}>
            <p style={{ fontFamily:U, fontSize:"clamp(14px,1.4vw,16px)", fontWeight:300, color:C.textMuted, lineHeight:1.8, margin:"0 0 16px" }}>
              Our mission is to provide the highest quality commercial driver training in California — training that meets federal standards, exceeds carrier expectations, and gives every graduate the knowledge, skill, and confidence to thrive on the road.
            </p>
            <p style={{ fontFamily:U, fontSize:"clamp(14px,1.4vw,16px)", fontWeight:300, color:C.textMuted, lineHeight:1.8, margin:0 }}>
              We serve veterans transitioning to civilian careers, individuals seeking a career change, and new entrants to the workforce — providing accessible, affordable, and fully funded pathways to a high-paying profession.
            </p>
          </div>
          {/* 2 top cards */}
          <div className="hp-grid-2-eq" style={{ marginBottom:"16px" }}>
            {[
              { img: facilityImg11,      title:"Federally Registered ELDT Provider", desc:"One of the first in California to meet the 2022 FMCSA mandate" },
              { img: facilityImg2,        title:"State-Licensed & DMV Certified",      desc:"Fully licensed by the California BPPE and certified by the DMV" },
            ].map((card, i) => (
              <div key={i} style={{ position:"relative", borderRadius:"8px", overflow:"hidden", height:"200px" }}>
                <img loading="lazy" decoding="async" src={card.img} alt={card.title} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
                <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)" }} />
                <div style={{ position:"absolute", bottom:"16px", left:"16px", right:"16px" }}>
                  <div style={{ fontFamily:U, fontSize:"14px", fontWeight:700, color:C.goldLight, marginBottom:"4px" }}>{card.title}</div>
                  <div style={{ fontFamily:U, fontSize:"12px", fontWeight:300, color:"rgba(255,255,255,0.75)" }}>{card.desc}</div>
                </div>
              </div>
            ))}
          </div>
          {/* 3 bottom cards */}
          <div className="hp-grid-3">
            {[
              { img: facilityImgStudents, title:"VA-Approved School",         desc:"Post-9/11 GI Bill accepted at all campus locations" },
              { img: facilityImg4,  title:"96% Graduate Job Placement", desc:"Active partnerships with regional and national carriers" },
              { img: facilityImg16, title:"Late-Model Training Fleet",  desc:"Full size tractors and 53-foot trailers at every campus" },
            ].map((card, i) => (
              <div key={i} style={{ position:"relative", borderRadius:"8px", overflow:"hidden", height:"200px" }}>
                <img loading="lazy" decoding="async" src={card.img} alt={card.title} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
                <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)" }} />
                <div style={{ position:"absolute", bottom:"16px", left:"16px", right:"16px" }}>
                  <div style={{ fontFamily:U, fontSize:"14px", fontWeight:700, color:C.goldLight, marginBottom:"4px" }}>{card.title}</div>
                  <div style={{ fontFamily:U, fontSize:"12px", fontWeight:300, color:"rgba(255,255,255,0.75)" }}>{card.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* â”€â”€ PROGRAMS â”€â”€ */}
      <section className="hp-section" style={{ background:"#071628" }}>
        <div className="hp-inner">
          <div style={{ marginBottom:"48px" }}>
            <SectionLabel text="Training Programs" />
            <h2 style={{ fontFamily:U, fontSize:"clamp(24px,3.5vw,42px)", fontWeight:800, color:"#FFFFFF", margin:"0 0 14px" }}>Programs Designed for Your Career</h2>
            <p style={{ fontFamily:U, fontSize:"clamp(14px,1.4vw,17px)", fontWeight:300, color:"rgba(255,255,255,0.6)", maxWidth:"500px", lineHeight:1.7 }}>Every program is structured around federal ELDT requirements with hands-on training using late-model full size tractors.</p>
          </div>
          <div className="hp-grid-programs">
            {programs.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} style={{ background:C.white, border:`1px solid ${C.border}`, borderTop:`3px solid ${C.gold}`, borderRadius:"4px", padding:"28px", position:"relative", transition:"box-shadow 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 40px rgba(27,43,75,0.1)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                  {p.badge && <div style={{ position:"absolute", top:"16px", right:"16px", fontFamily:U, fontSize:"10px", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:C.white, background:C.gold, padding:"4px 10px", borderRadius:"2px" }}>{p.badge}</div>}
                  <div style={{ width:"44px", height:"44px", background:C.lightGray, borderRadius:"4px", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:"20px" }}><Icon size={20} color={C.navy}/></div>
                  <div style={{ fontFamily:U, fontSize:"11px", fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase", color:C.gold, marginBottom:"6px" }}>{p.subtitle}</div>
                  <h3 style={{ fontFamily:U, fontSize:"20px", fontWeight:800, color:C.navy, margin:"0 0 12px" }}>{p.title}</h3>
                  <p style={{ fontFamily:U, fontSize:"14px", fontWeight:300, color:C.textMuted, lineHeight:1.7, margin:"0 0 20px" }}>{p.desc}</p>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", paddingTop:"16px", borderTop:`1px solid ${C.border}` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:"6px" }}><Clock size={13} color={C.midGray}/><span style={{ fontFamily:U, fontSize:"13px", color:C.textMuted }}>{p.duration}</span></div>
                    <a href="/apply" style={{ display:"flex", alignItems:"center", gap:"6px", fontFamily:U, fontSize:"13px", fontWeight:600, color:C.navy, textDecoration:"none" }}>Enroll <ChevronRight size={13}/></a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* â”€â”€ TUITION CALCULATOR â”€â”€ */}
      <section className="hp-section" style={{ background:"#EEF3FA" }}>
        <div className="hp-inner">
          <div className="hp-calc-grid">
            <div>
              <SectionLabel text="Tuition & Financing" />
              <h2 style={{ fontFamily:U, fontSize:"clamp(24px,3.5vw,40px)", fontWeight:800, color:C.navy, margin:"0 0 14px", lineHeight:1.2 }}>
                Most Students Pay<br /><span style={{ color:C.navy }}>Zero Out of Pocket.</span>
              </h2>
              <p style={{ fontFamily:U, fontSize:"clamp(14px,1.4vw,16px)", fontWeight:300, color:C.textMuted, lineHeight:1.8, margin:"0 0 24px" }}>
                Between GI Bill benefits, federal WIOA grants, and employer sponsorships — there's a funding path for everyone.
              </p>
              <a href="/financing" style={{ display:"inline-flex", alignItems:"center", gap:8, fontFamily:U, fontSize:13, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:"#FFFFFF", background:C.navy, padding:"12px 24px", borderRadius:3, textDecoration:"none" }}>
                View All Financing Options
              </a>
            </div>
            <TuitionCalculator compact />
          </div>
        </div>
      </section>


{/* â”€â”€ CORPORATE PARTNERS â”€â”€ */}
      <section className="hp-section" style={{ background: C.offWhite }}>
        <div className="hp-inner">
          <div style={{
            background: `linear-gradient(135deg, ${C.navyDark} 0%, ${C.navy} 60%, #243860 100%)`,
            borderRadius: "16px",
            padding: "clamp(40px,6vw,72px) clamp(28px,5vw,64px)",
            position: "relative",
            overflow: "hidden",
          }}>
            {/* background accents */}
            <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle at 90% 50%, rgba(168,184,200,0.10) 0%, transparent 55%), radial-gradient(circle at 10% 80%, rgba(168,184,200,0.06) 0%, transparent 40%)`, pointerEvents:"none" }} />
            <div style={{ position:"absolute", top:0, left:0, bottom:0, width:"4px", background:`linear-gradient(to bottom, transparent, ${C.gold}, transparent)` }} />

            <div style={{ position:"relative", zIndex:1, display:"flex", alignItems:"center", justifyContent:"space-between", gap:"40px", flexWrap:"wrap" }}>

              {/* Left: text */}
              <div style={{ flex:"1 1 340px" }}>
                <div style={{ display:"inline-flex", alignItems:"center", gap:"8px", background:"rgba(168,184,200,0.12)", border:"1px solid rgba(168,184,200,0.25)", borderRadius:"4px", padding:"5px 14px", marginBottom:"20px" }}>
                  <Building2 size={13} color={C.gold} />
                  <span style={{ fontFamily:U, fontSize:"11px", fontWeight:700, letterSpacing:"0.14em", textTransform:"uppercase", color:C.gold }}>Corporate Partnership</span>
                </div>

                <h2 style={{ fontFamily:U, fontSize:"clamp(26px,3.5vw,46px)", fontWeight:900, color:C.white, lineHeight:1.1, margin:"0 0 16px" }}>
                  Become a<br />
                  <span style={{ color:C.gold }}>Location Partner.</span>
                </h2>

                <p style={{ fontFamily:U, fontSize:"clamp(14px,1.3vw,16px)", fontWeight:300, color:"rgba(255,255,255,0.65)", lineHeight:1.8, margin:"0 0 28px", maxWidth:"480px" }}>
                  Partner with California School of Trucking to sponsor CDL training at your location. Get direct access to a pipeline of job-ready, licensed drivers while we handle the curriculum, compliance, and certification.
                </p>

                <div style={{ display:"flex", flexDirection:"column", gap:"10px", marginBottom:"32px" }}>
                  {[
                    "Train drivers at your facility — we bring the program to you",
                    "Tax-deductible sponsorships through Rich Center for Prosperity (501c3)",
                    "Priority hiring access to our certified graduates",
                    "Brand placement and featured exposure on the CST platform",
                  ].map((item, i) => (
                    <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:"10px" }}>
                      <CheckCircle size={15} color={C.gold} style={{ flexShrink:0, marginTop:"2px" }} />
                      <span style={{ fontFamily:U, fontSize:"14px", color:"rgba(255,255,255,0.75)", lineHeight:1.6 }}>{item}</span>
                    </div>
                  ))}
                </div>

                <div style={{ display:"flex", gap:"12px", flexWrap:"wrap" }}>
                  <a href="/corporate-partners" style={{
                    display:"inline-flex", alignItems:"center", gap:"8px",
                    fontFamily:U, fontSize:"14px", fontWeight:800,
                    letterSpacing:"0.08em", textTransform:"uppercase",
                    color:C.navyDark, background:C.gold,
                    padding:"14px 32px", borderRadius:"4px", textDecoration:"none",
                    boxShadow:"0 6px 28px rgba(168,184,200,0.3)",
                    transition:"background 0.2s, transform 0.15s",
                  }}
                    onMouseEnter={e=>{ e.currentTarget.style.background="#FFFFFF"; e.currentTarget.style.transform="translateY(-2px)"; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background=C.gold; e.currentTarget.style.transform="translateY(0)"; }}
                  >
                    Become a Partner <ArrowRight size={16} />
                  </a>
                  <a href="/contact" style={{
                    display:"inline-flex", alignItems:"center", gap:"8px",
                    fontFamily:U, fontSize:"14px", fontWeight:600,
                    color:"rgba(255,255,255,0.65)",
                    background:"rgba(255,255,255,0.06)",
                    border:"1px solid rgba(255,255,255,0.18)",
                    padding:"14px 28px", borderRadius:"4px", textDecoration:"none",
                    transition:"background 0.2s",
                  }}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.12)"}
                    onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}
                  >
                    Talk to Us
                  </a>
                </div>
              </div>

              {/* Right: stat tiles */}
              <div style={{ flex:"0 0 auto", display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px", width:"clamp(240px,30vw,320px)" }}>
                {[
                  { value:"50–100", label:"Veteran drivers trained per year per partner", icon:Users },
                  { value:"501(c)3", label:"Tax-deductible sponsorship through Rich Center for Prosperity", icon:Shield },
                  { value:"92%",    label:"Veteran job placement rate within 30 days", icon:TrendingUp },
                  { value:"$0",     label:"Out-of-pocket cost for sponsored veteran trainees", icon:DollarSign },
                ].map((stat, i) => {
                  const Icon = stat.icon;
                  return (
                    <div key={i} style={{ background:"rgba(255,255,255,0.05)", border:"1px solid rgba(168,184,200,0.15)", borderRadius:"8px", padding:"20px 16px", textAlign:"center" }}>
                      <Icon size={18} color={C.gold} style={{ marginBottom:"10px" }} />
                      <div style={{ fontFamily:U, fontSize:"clamp(18px,2vw,26px)", fontWeight:900, color:C.white, lineHeight:1, marginBottom:"6px" }}>{stat.value}</div>
                      <div style={{ fontFamily:U, fontSize:"11px", fontWeight:300, color:"rgba(255,255,255,0.40)", lineHeight:1.5 }}>{stat.label}</div>
                    </div>
                  );
                })}
              </div>

            </div>
          </div>
        </div>
      </section>

{/* â”€â”€ GIFT CERTIFICATES â”€â”€ */}
<section className="hp-section" style={{ background:"#071628" }}>
  <div className="hp-inner">
    <div style={{ background:C.navy, borderRadius:"16px", padding:"clamp(32px,5vw,64px)", display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle at 80% 50%, rgba(168,184,200,0.12) 0%, transparent 60%)`, pointerEvents:"none" }} />
      <div style={{ display:"inline-flex", alignItems:"center", gap:"10px", marginBottom:"16px" }}>
        <div style={{ width:"32px", height:"2px", background:C.gold }} />
        <span style={{ fontFamily:U, fontSize:"12px", fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", color:C.gold }}>Gift Certificates</span>
        <div style={{ width:"32px", height:"2px", background:C.gold }} />
      </div>
      <h2 style={{ fontFamily:U, fontSize:"clamp(24px,3.5vw,42px)", fontWeight:900, color:C.white, margin:"0 0 14px", lineHeight:1.1 }}>
        Give the Gift of a <span style={{ color:C.goldLight }}>Career.</span>
      </h2>
      <p style={{ fontFamily:U, fontSize:"clamp(14px,1.4vw,17px)", fontWeight:300, color:"rgba(255,255,255,0.65)", lineHeight:1.8, maxWidth:"520px", margin:"0 0 32px" }}>
        Makes a great graduation gift for high school students. CST Gift Certificates are valid toward any program or service.
      </p>
      <div style={{ display:"flex", gap:"12px", flexWrap:"wrap", justifyContent:"center" }}>
        <a href="/gift-certificates" style={{ display:"inline-flex", alignItems:"center", gap:"8px", fontFamily:U, fontSize:"14px", fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:C.navyDark, background:C.gold, padding:"14px 32px", borderRadius:"6px", textDecoration:"none" }}>
          Buy a Gift Certificate
        </a>
        <span style={{ display:"inline-flex", alignItems:"center", gap:"8px", fontFamily:U, fontSize:"14px", fontWeight:600, color:"rgba(255,255,255,0.35)", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", padding:"14px 28px", borderRadius:"6px" }}>
          +1 (818) 438-3604
        </span>
      </div>
    </div>
  </div>
</section>

      {/* â”€â”€ ROAD READY MANUAL PROMO â”€â”€ */}
      <section className="hp-section" style={{ background:"#050E1C", position:"relative", overflow:"hidden" }}>
        {/* background glow accents */}
        <div style={{ position:"absolute", inset:0, pointerEvents:"none", backgroundImage:"radial-gradient(ellipse 60% 50% at 70% 50%, rgba(168,184,200,0.07) 0%, transparent 70%), radial-gradient(ellipse 40% 60% at 10% 80%, rgba(168,184,200,0.04) 0%, transparent 60%)" }} />

        <div className="hp-inner" style={{ position:"relative", zIndex:1 }}>
          <div className="hp-grid-2">

            {/* Left — book image */}
            <div style={{ display:"flex", justifyContent:"center", alignItems:"center" }}>
              <div style={{ position:"relative", maxWidth:"380px", width:"100%" }}>
                {/* glow behind book */}
                <div style={{ position:"absolute", inset:"-20px", background:"radial-gradient(ellipse 80% 80% at 50% 50%, rgba(168,184,200,0.18) 0%, transparent 70%)", borderRadius:"50%", filter:"blur(24px)", pointerEvents:"none" }} />
                <img
                  src={roadReadyManualImg}
                  alt="CST Road Ready Manual 2026"
                  style={{
                    width:"100%", height:"auto", display:"block",
                    borderRadius:"6px",
                    boxShadow:"0 32px 80px rgba(0,0,0,0.7), 0 8px 24px rgba(0,0,0,0.5)",
                    transform:"perspective(900px) rotateY(6deg) rotateX(2deg)",
                    position:"relative", zIndex:1,
                  }}
                />
                {/* available now ribbon */}
                <div style={{
                  position:"absolute", top:"-14px", right:"-14px", zIndex:2,
                  background:`linear-gradient(135deg, #B8860B 0%, ${C.gold} 50%, #B8860B 100%)`,
                  color:"#071628", fontFamily:U, fontSize:"10px", fontWeight:900,
                  letterSpacing:"0.16em", textTransform:"uppercase",
                  padding:"6px 16px", borderRadius:"3px",
                  boxShadow:"0 4px 16px rgba(168,184,200,0.4)",
                }}>
                  Available Now
                </div>
              </div>
            </div>

            {/* Right — copy */}
            <div>
              {/* label */}
              <div style={{ display:"inline-flex", alignItems:"center", gap:"8px", background:"rgba(168,184,200,0.1)", border:"1px solid rgba(168,184,200,0.25)", borderRadius:"4px", padding:"5px 14px", marginBottom:"20px" }}>
                <BookOpen size={13} color={C.gold} />
                <span style={{ fontFamily:U, fontSize:"11px", fontWeight:700, letterSpacing:"0.14em", textTransform:"uppercase", color:C.gold }}>New Release</span>
              </div>

              <h2 style={{ fontFamily:U, fontSize:"clamp(26px,3.8vw,50px)", fontWeight:900, color:C.white, lineHeight:1.05, margin:"0 0 8px", letterSpacing:"-0.02em" }}>
                CST Road Ready<br />
                <span style={{ color:C.gold }}>Manual 2026</span>
              </h2>
              <p style={{ fontFamily:U, fontSize:"clamp(13px,1.2vw,15px)", fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase", color:"rgba(255,255,255,0.45)", margin:"0 0 20px" }}>
                Trained. Certified. Ready. Your Next Mission Starts Here.
              </p>
              <p style={{ fontFamily:U, fontSize:"clamp(14px,1.3vw,16px)", fontWeight:300, color:"rgba(255,255,255,0.65)", lineHeight:1.8, margin:"0 0 28px" }}>
                The definitive guide for new and aspiring CDL drivers — built by Otis Lee Cooper Jr. and the California School of Trucking team. Covers everything from FMCSA compliance and pre-trip inspections to cargo securement, mountain driving, and real-world road readiness.
              </p>

              <div style={{ display:"flex", flexDirection:"column", gap:"10px", marginBottom:"32px" }}>
                {[
                  "Full Road Ready Program — 10 Weeks to a Better Future",
                  "Ages 18 to 50 — experience welcome, potential is key",
                  "Veteran-focused content backed by decades of field experience",
                  "Stay compliant — understand the rules, avoid the risks",
                ].map((item, i) => (
                  <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:"10px" }}>
                    <CheckCircle size={15} color={C.gold} style={{ flexShrink:0, marginTop:"3px" }} />
                    <span style={{ fontFamily:U, fontSize:"14px", color:"rgba(255,255,255,0.72)", lineHeight:1.6 }}>{item}</span>
                  </div>
                ))}
              </div>

              <div style={{ display:"flex", gap:"12px", flexWrap:"wrap", alignItems:"center" }}>
                <a
                  href="/booklets"
                  style={{
                    display:"inline-flex", alignItems:"center", gap:"8px",
                    fontFamily:U, fontSize:"13px", fontWeight:800,
                    letterSpacing:"0.1em", textTransform:"uppercase",
                    color:"#071628", background:C.gold,
                    padding:"14px 32px", borderRadius:"4px", textDecoration:"none",
                    boxShadow:"0 6px 28px rgba(168,184,200,0.3)",
                    transition:"background 0.2s, transform 0.15s",
                  }}
                  onMouseEnter={e=>{ e.currentTarget.style.background="#FFFFFF"; e.currentTarget.style.transform="translateY(-2px)"; }}
                  onMouseLeave={e=>{ e.currentTarget.style.background=C.gold; e.currentTarget.style.transform="translateY(0)"; }}
                >
                  Order Now <ChevronRight size={15} />
                </a>
                <span style={{ fontFamily:U, fontSize:"12px", fontWeight:500, color:"rgba(255,255,255,0.35)", letterSpacing:"0.04em" }}>
                  Available at caschooloftrucking.com
                </span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* â”€â”€ WHY CHOOSE US â”€â”€ */}
      <section className="hp-section" style={{ background:C.offWhite }}>
        <div className="hp-inner">
          <div className="hp-grid-2">
            <div>
              <SectionLabel text="Why Choose Us" />
              <h2 style={{ fontFamily:U, fontSize:"clamp(24px,3.5vw,40px)", fontWeight:800, color:C.navy, margin:"0 0 16px", lineHeight:1.2 }}>
                Built by an Expert.<br />Designed for Your Success.
              </h2>
              <p style={{ fontFamily:U, fontSize:"clamp(14px,1.4vw,16px)", fontWeight:300, color:C.textMuted, lineHeight:1.8, margin:"0 0 28px" }}>
                California School of Trucking is led by a founder with 34 years of hands-on industry experience — bringing real-world knowledge, professional standards, and genuine commitment to every student's career.
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
                {["Federally registered ELDT provider","State-licensed campus operations","Late-model Freightliner training fleet","On-site CDL skills testing at select campuses"].map((item,i)=>(
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:"12px" }}>
                    <CheckCircle size={16} color={C.gold}/>
                    <span style={{ fontFamily:U, fontSize:"14px", color:C.darkGray }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="hp-grid-features">
              {features.map((f,i)=>{ const Icon=f.icon; return(
                <div key={i} style={{ background:"#FFFFFF", border:`1px solid ${C.border}`, borderRadius:"4px", padding:"20px", boxShadow:"0 2px 12px rgba(27,43,75,0.08)" }}>
                  <Icon size={20} color={C.gold} style={{ marginBottom:"12px" }}/>
                  <h4 style={{ fontFamily:U, fontSize:"13px", fontWeight:700, color:C.navy, margin:"0 0 6px" }}>{f.title}</h4>
                  <p style={{ fontFamily:U, fontSize:"12px", fontWeight:300, color:C.textMuted, lineHeight:1.65, margin:0 }}>{f.desc}</p>
                </div>
              );})}
            </div>
          </div>
        </div>
      </section>

      {/* â”€â”€ MEET THE TEAM â”€â”€ */}
      <section className="hp-section" style={{ background:C.dark1 }}>
        <div className="hp-inner">
          <div style={{ textAlign:"center", marginBottom:"48px" }}>
            <SectionLabel text="Meet the Team" center />
            <h2 style={{ fontFamily:U, fontSize:"clamp(24px,3.5vw,40px)", fontWeight:800, color:C.white, margin:"0 0 12px" }}>The People Behind CST</h2>
            <p style={{ fontFamily:U, fontSize:"clamp(14px,1.4vw,16px)", fontWeight:300, color:"rgba(255,255,255,0.5)", maxWidth:"480px", margin:"0 auto", lineHeight:1.75 }}>
              Experienced professionals dedicated to building California's next generation of commercial drivers.
            </p>
          </div>
          <div className="hp-grid-team">
            {team.map((member,i)=>(
              <div key={i} style={{ borderRadius:"12px", overflow:"hidden", boxShadow:"0 8px 32px rgba(0,0,0,0.4)", transition:"transform 0.2s,box-shadow 0.2s", cursor:"pointer", background:C.dark2 }}
                onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-4px)"; e.currentTarget.style.boxShadow="0 16px 48px rgba(0,0,0,0.5)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 8px 32px rgba(0,0,0,0.4)"; }}>
                <div style={{ width:"100%", height:"300px", position:"relative", overflow:"hidden" }}>
                  {member.photo
                    ? <img loading="lazy" decoding="async" src={member.photo} alt={member.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top", display:"block" }}/>
                    : <div style={{ width:"100%", height:"100%", background:`linear-gradient(135deg,${member.accent}30,${member.accent}10)`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <span style={{ fontFamily:U, fontSize:"60px", fontWeight:900, color:member.accent, opacity:0.6 }}>{member.initials}</span>
                      </div>
                  }
                  <div style={{ position:"absolute", bottom:0, left:0, right:0, height:"60%", background:"linear-gradient(to top,rgba(0,0,0,0.92) 0%,rgba(0,0,0,0.4) 60%,transparent 100%)" }}/>
                  <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"16px" }}>
                    <div style={{ display:"inline-flex", alignItems:"center", background:`${member.accent}20`, border:`1px solid ${member.accent}40`, borderRadius:"20px", padding:"3px 10px", marginBottom:"6px" }}>
                      <span style={{ fontFamily:U, fontSize:"10px", fontWeight:700, color:member.accent, letterSpacing:"0.06em", textTransform:"uppercase" }}>{member.exp}</span>
                    </div>
                    <h3 style={{ fontFamily:U, fontSize:"17px", fontWeight:800, color:C.white, margin:"0 0 2px", lineHeight:1.2 }}>{member.name}</h3>
                    <p style={{ fontFamily:U, fontSize:"11px", fontWeight:600, color:member.accent, margin:0 }}>{member.title}</p>
                  </div>
                </div>
                <div style={{ padding:"16px 18px", borderTop:`2px solid ${member.accent}` }}>
  <p style={{
    fontFamily:U, fontSize:"13px", fontWeight:300,
    color:"rgba(255,255,255,0.55)", lineHeight:1.7, margin:0,
    display:"-webkit-box", WebkitLineClamp:4,
    WebkitBoxOrient:"vertical", overflow:"hidden",
    textAlign:"justify"
  }}>
    {member.bio}
  </p>
  {member.bio.length > 280 && (
    <button
      onClick={() => setBioModal(member)}
      style={{
        marginTop:"10px", background:"none",
        border:`1px solid ${member.accent}50`, borderRadius:"3px",
        padding:"6px 14px", cursor:"pointer", fontFamily:U,
        fontSize:"11px", fontWeight:600, letterSpacing:"0.08em",
        textTransform:"uppercase", color:member.accent,
        display:"block", width:"100%", transition:"background 0.15s"
      }}
      onMouseEnter={e => e.currentTarget.style.background = `${member.accent}15`}
      onMouseLeave={e => e.currentTarget.style.background = "none"}
    >
      Read Full Bio
    </button>
  )}
</div>
              </div>
            ))}
          </div>

          <div style={{ textAlign:"center", marginTop:"48px" }}>
            <button onClick={()=>setShowTeam(v=>!v)} style={{ display:"inline-flex", alignItems:"center", gap:"10px", fontFamily:U, fontSize:"13px", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:C.dark1, background:C.gold, padding:"14px 28px", borderRadius:"3px", border:"none", cursor:"pointer", transition:"background 0.2s" }}
              onMouseEnter={e=>e.currentTarget.style.background=C.goldLight}
              onMouseLeave={e=>e.currentTarget.style.background=C.gold}>
              {showTeam ? "▲  Hide Team" : "▼  Special Thanks — Meet Our Team"}
            </button>
          </div>

          {showTeam && (
            <div style={{ marginTop:"56px" }}>
              <div style={{ textAlign:"center", marginBottom:"40px" }}>
                <h3 style={{ fontFamily:U, fontSize:"clamp(20px,2.8vw,32px)", fontWeight:800, color:C.white, margin:"0 0 10px" }}>Special Thanks to Our Team</h3>
                <p style={{ fontFamily:U, fontSize:"14px", fontWeight:300, color:"rgba(255,255,255,0.45)", maxWidth:"440px", margin:"0 auto", lineHeight:1.75 }}>The dedicated individuals who work behind the scenes to make CST's mission possible.</p>
              </div>
              <div className="hp-grid-support">
                {supportTeam.map((member,i)=>(
                  <div key={i} style={{ background:C.dark2, border:"1px solid rgba(255,255,255,0.08)", borderTop:`3px solid ${member.accent}`, borderRadius:"8px", overflow:"hidden", transition:"transform 0.2s,box-shadow 0.2s" }}
                    onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow="0 12px 36px rgba(0,0,0,0.4)"; }}
                    onMouseLeave={e=>{ e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="none"; }}>
                    <div style={{ width:"100%", height:"180px", position:"relative", overflow:"hidden" }}>
                      {member.photo
                        ? <img loading="lazy" decoding="async" src={member.photo} alt={member.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top", display:"block" }}/>
                        : <div style={{ width:"100%", height:"100%", background:`linear-gradient(135deg,${member.accent}25,${member.accent}08)`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                            <span style={{ fontFamily:U, fontSize:"48px", fontWeight:900, color:member.accent, opacity:0.55 }}>{member.initials}</span>
                          </div>
                      }
                      <div style={{ position:"absolute", bottom:0, left:0, right:0, height:"55%", background:"linear-gradient(to top,rgba(0,0,0,0.88) 0%,transparent 100%)" }}/>
                      <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"10px 12px" }}>
                        <h4 style={{ fontFamily:U, fontSize:"14px", fontWeight:800, color:C.white, margin:"0 0 2px", lineHeight:1.2 }}>{member.name}</h4>
                        <p style={{ fontFamily:U, fontSize:"11px", fontWeight:600, color:member.accent, margin:0 }}>{member.title}</p>
                      </div>
                    </div>
                    <div style={{ padding:"12px 14px", borderTop:`1px solid ${member.accent}30` }}>
  <p style={{
    fontFamily:U, fontSize:"12px", fontWeight:300,
    color:"rgba(255,255,255,0.5)", lineHeight:1.65, margin:0,
    display:"-webkit-box", WebkitLineClamp:3,
    WebkitBoxOrient:"vertical", overflow:"hidden",
    textAlign:"justify"
  }}>
    {member.bio}
  </p>
  {member.bio.length > 200 && (
    <button
      onClick={() => setBioModal(member)}
      style={{
        marginTop:"8px", background:"none",
        border:`1px solid ${member.accent}50`, borderRadius:"3px",
        padding:"5px 12px", cursor:"pointer", fontFamily:U,
        fontSize:"10px", fontWeight:600, letterSpacing:"0.08em",
        textTransform:"uppercase", color:member.accent,
        display:"block", width:"100%", transition:"background 0.15s"
      }}
      onMouseEnter={e => e.currentTarget.style.background = `${member.accent}15`}
      onMouseLeave={e => e.currentTarget.style.background = "none"}
    >
      Read Full Bio
    </button>
  )}
</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* â”€â”€ TESTIMONIALS â”€â”€ */}
      <TestimonialsSection />

      {/* â”€â”€ BLOG â”€â”€ */}
      <section className="hp-section" style={{ background:"#EEF3FA" }}>
        <div className="hp-inner">
          <div className="hp-blog-header">
            <div>
              <SectionLabel text="Resources & Insights" />
              <h2 style={{ fontFamily:U, fontSize:"clamp(24px,3.5vw,40px)", fontWeight:800, color:C.navy, margin:0 }}>Latest Articles</h2>
            </div>
            <a href="/blog" style={{ fontFamily:U, fontSize:"13px", fontWeight:600, color:C.gold, textDecoration:"none", display:"flex", alignItems:"center", gap:"6px", flexShrink:0 }}>View All <ChevronRight size={14}/></a>
          </div>
          <div className="hp-grid-blog">
            {newsLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} style={{ border:`1px solid ${C.border}`, borderRadius:"4px", overflow:"hidden", background:"#FFFFFF" }}>
                    <div style={{ height:"160px", background:`linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, opacity:0.4 }} />
                    <div style={{ padding:"22px" }}>
                      <div style={{ height:"10px", background:C.lightGray, borderRadius:"3px", width:"40%", marginBottom:"12px" }} />
                      <div style={{ height:"14px", background:C.lightGray, borderRadius:"3px", width:"90%", marginBottom:"8px" }} />
                      <div style={{ height:"14px", background:C.lightGray, borderRadius:"3px", width:"70%", marginBottom:"16px" }} />
                    </div>
                  </div>
                ))
              : newsArticles.length > 0
                ? newsArticles.map((a, i) => (
                    <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                      style={{ textDecoration:"none", display:"flex", flexDirection:"column" }}>
                      <article style={{ border:`1px solid ${C.border}`, borderRadius:"4px", overflow:"hidden", transition:"box-shadow 0.2s, transform 0.2s", cursor:"pointer", background:"#FFFFFF", display:"flex", flexDirection:"column", height:"100%" }}
                        onMouseEnter={e=>{ e.currentTarget.style.boxShadow="0 8px 40px rgba(27,43,75,0.1)"; e.currentTarget.style.transform="translateY(-2px)"; }}
                        onMouseLeave={e=>{ e.currentTarget.style.boxShadow="none"; e.currentTarget.style.transform="translateY(0)"; }}>
                        <div style={{ height:"160px", overflow:"hidden", background:C.offWhite, flexShrink:0 }}>
                          {a.urlToImage
                            ? <img loading="lazy" decoding="async" src={a.urlToImage} alt={a.title} style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e=>{ e.target.style.display="none"; }} />
                            : <div style={{ height:"100%", background:`linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, display:"flex", alignItems:"center", justifyContent:"center" }}><BookOpen size={28} color="rgba(255,255,255,0.25)"/></div>
                          }
                        </div>
                        <div style={{ padding:"22px", display:"flex", flexDirection:"column", flex:1 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"10px" }}>
                            <span style={{ fontFamily:U, fontSize:"10px", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:"#fff", background:C.navy, borderRadius:"3px", padding:"2px 8px" }}>{a.source?.name || 'News'}</span>
                            <span style={{ fontFamily:U, fontSize:"11px", color:C.midGray, display:"flex", alignItems:"center", gap:"3px" }}><Clock size={10}/> {a.publishedAt ? new Date(a.publishedAt).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : ''}</span>
                          </div>
                          <h3 style={{ fontFamily:U, fontSize:"15px", fontWeight:800, color:C.navy, margin:"0 0 10px", lineHeight:1.4, display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{a.title}</h3>
                          <p style={{ fontFamily:U, fontSize:"13px", fontWeight:300, color:C.textMuted, lineHeight:1.7, margin:"0", flex:1, display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{a.description}</p>
                          <div style={{ display:"flex", alignItems:"center", gap:"4px", paddingTop:"14px", marginTop:"14px", borderTop:`1px solid ${C.border}` }}>
                            <span style={{ fontFamily:U, fontSize:"12px", fontWeight:600, color:C.navy, display:"flex", alignItems:"center", gap:"4px" }}>Read Article <ExternalLink size={12}/></span>
                          </div>
                        </div>
                      </article>
                    </a>
                  ))
                : blogs.map((b, i) => (
                    <article key={i} style={{ border:`1px solid ${C.border}`, borderRadius:"4px", overflow:"hidden", transition:"box-shadow 0.2s", cursor:"pointer", background:"#FFFFFF" }}
                      onMouseEnter={e=>e.currentTarget.style.boxShadow="0 8px 40px rgba(27,43,75,0.1)"}
                      onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
                      <div style={{ height:"140px", background:`linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <BookOpen size={28} color="rgba(255,255,255,0.25)"/>
                      </div>
                      <div style={{ padding:"22px" }}>
                        <span style={{ fontFamily:U, fontSize:"10px", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:C.gold }}>{b.category}</span>
                        <h3 style={{ fontFamily:U, fontSize:"16px", fontWeight:800, color:C.navy, margin:"8px 0 10px", lineHeight:1.4 }}>{b.title}</h3>
                        <p style={{ fontFamily:U, fontSize:"13px", fontWeight:300, color:C.textMuted, lineHeight:1.7, margin:"0 0 16px" }}>{b.excerpt}</p>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:"14px", borderTop:`1px solid ${C.border}` }}>
                          <span style={{ fontFamily:U, fontSize:"11px", color:C.textMuted }}>{b.date}</span>
                          <span style={{ fontFamily:U, fontSize:"12px", fontWeight:600, color:C.navy, display:"flex", alignItems:"center", gap:"4px" }}>Read More <ChevronRight size={12}/></span>
                        </div>
                      </div>
                    </article>
                  ))
            }
          </div>
        </div>
      </section>

      {/* â”€â”€ FAQ â”€â”€ */}
      <section className="hp-section" style={{ background:C.dark1 }}>
        <div className="hp-inner-xs">
          <div style={{ marginBottom:"48px", textAlign:"center" }}>
            <SectionLabel text="Frequently Asked Questions" center />
            <h2 style={{ fontFamily:U, fontSize:"clamp(24px,3.5vw,40px)", fontWeight:800, color:C.white, margin:0 }}>Common Questions</h2>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:"2px" }}>
            {faqs.map((f,i)=>(
              <div key={i} style={{ background:C.dark2, border:"1px solid rgba(168,184,200,0.15)", borderRadius:"4px", overflow:"hidden" }}>
                <button onClick={()=>setOpen(open===i?null:i)} style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", padding:"18px 20px", background:"none", border:"none", cursor:"pointer", textAlign:"left" }}>
                  <span style={{ fontFamily:U, fontSize:"clamp(14px,1.4vw,16px)", fontWeight:500, color:C.white, paddingRight:"12px" }}>{f.q}</span>
                  <ChevronDown size={18} color={C.gold} style={{ transform:open===i?"rotate(180deg)":"none", transition:"transform 0.2s", flexShrink:0 }}/>
                </button>
                {open===i&&(
                  <div style={{ padding:"0 20px 20px", borderTop:"1px solid rgba(168,184,200,0.15)" }}>
                    <p style={{ fontFamily:U, fontSize:"14px", fontWeight:300, color:"rgba(255,255,255,0.6)", lineHeight:1.75, margin:"14px 0 0" }}>{f.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* â”€â”€ CTA â”€â”€ */}
      <section className="hp-section" style={{ background:"#EEF3FA", position:"relative", overflow:"hidden" }}>
        <div style={{ maxWidth:"760px", margin:"0 auto", textAlign:"center", position:"relative", zIndex:1 }}>
          <SectionLabel text="Begin Your Journey" center />
          <h2 style={{ fontFamily:U, fontSize:"clamp(24px,4vw,46px)", fontWeight:900, color:C.navy, margin:"0 0 16px", lineHeight:1.2 }}>Ready to Start Your Career in Commercial Driving?</h2>
          <p style={{ fontFamily:U, fontSize:"clamp(14px,1.4vw,17px)", fontWeight:300, color:C.textMuted, lineHeight:1.75, margin:"0 0 36px" }}>Classes begin every Monday. Speak with an admissions advisor today to review your eligibility, financing options, and program selection.</p>
          <div style={{ display:"flex", gap:"14px", justifyContent:"center", flexWrap:"wrap" }}>
            <a href="/apply" style={{ display:"inline-flex", alignItems:"center", gap:"8px", fontFamily:U, fontSize:"14px", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:"#FFFFFF", background:C.navy, padding:"14px 32px", borderRadius:"3px", textDecoration:"none" }}>
              <GraduationCap size={16}/> Apply Now
            </a>
            <span style={{ display:"inline-flex", alignItems:"center", gap:"8px", fontFamily:U, fontSize:"14px", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:"rgba(27,43,75,0.4)", padding:"14px 32px", borderRadius:"3px", border:"1px solid rgba(27,43,75,0.15)" }}>
              <Phone size={16}/> +1 (818) 438-3604
            </span>
            <a href="/gift-certificates" style={{ display:"inline-flex", alignItems:"center", gap:"8px", fontFamily:U, fontSize:"14px", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:C.navy, background:"transparent", padding:"14px 32px", borderRadius:"3px", textDecoration:"none", border:`1px solid ${C.navy}` }}>
              <Gift size={16}/> Gift Certificates
            </a>
          </div>
        </div>
      </section>

      {/* â”€â”€ AD FOOTER â”€â”€ */}
      <div style={{ display:"flex", justifyContent:"center", padding:"20px 16px", overflowX:"hidden", background:"#071628" }}>
        <AdSlot placement="footer_banner" width={728} height={90} />
      </div>

            {bioModal && (
  <div
    onClick={() => setBioModal(null)}
    style={{
      position:"fixed", inset:0, zIndex:9999,
      background:"rgba(0,0,0,0.75)", backdropFilter:"blur(6px)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"24px"
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background:C.dark2, borderRadius:"12px", maxWidth:"560px",
        width:"100%", maxHeight:"85vh", overflowY:"auto",
        border:`1px solid ${bioModal.accent}40`,
        boxShadow:"0 24px 80px rgba(0,0,0,0.7)"
      }}
    >
      <div style={{ position:"relative", height:"300px", overflow:"hidden", borderRadius:"12px 12px 0 0" }}>
        {bioModal.photo
          ? <img src={bioModal.photo} alt={bioModal.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center 20%" }}/>
          : <div style={{ width:"100%", height:"100%", background:`linear-gradient(135deg,${bioModal.accent}30,${bioModal.accent}10)`, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <span style={{ fontFamily:U, fontSize:"72px", fontWeight:900, color:bioModal.accent, opacity:0.5 }}>{bioModal.initials}</span>
            </div>
        }
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)" }}/>
        <button
          onClick={() => setBioModal(null)}
          style={{
            position:"absolute", top:"12px", right:"12px",
            background:"rgba(0,0,0,0.5)", border:"1px solid rgba(255,255,255,0.2)",
            borderRadius:"50%", width:"32px", height:"32px", cursor:"pointer",
            color:C.white, fontFamily:U, fontSize:"18px",
            display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1
          }}
        >×</button>
        <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"20px 24px" }}>
          <div style={{ display:"inline-flex", alignItems:"center", background:`${bioModal.accent}20`, border:`1px solid ${bioModal.accent}40`, borderRadius:"20px", padding:"3px 10px", marginBottom:"8px" }}>
            <span style={{ fontFamily:U, fontSize:"10px", fontWeight:700, color:bioModal.accent, letterSpacing:"0.06em", textTransform:"uppercase" }}>{bioModal.exp}</span>
          </div>
          <h3 style={{ fontFamily:U, fontSize:"22px", fontWeight:800, color:C.white, margin:"0 0 4px" }}>{bioModal.name}</h3>
          <p style={{ fontFamily:U, fontSize:"13px", fontWeight:600, color:bioModal.accent, margin:0 }}>{bioModal.title}</p>
        </div>
      </div>
      <div style={{ padding:"28px 28px 32px", borderTop:`2px solid ${bioModal.accent}` }}>
        <p style={{ fontFamily:U, fontSize:"14px", fontWeight:300, color:"rgba(255,255,255,0.72)", lineHeight:1.85, margin:0, textAlign:"justify", whiteSpace:"pre-wrap" }}>
          {bioModal.bio}
        </p>
      </div>
    </div>
  </div>
)}

    </>

  );
}